import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Plus, Search, Edit2, Trash2, Check, X, ChevronRight, Package } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';

// ─── Inline Edit Input ────────────────────────────────────────────────────────
function InlineEdit({ value, onSave, onCancel }) {
  const [val, setVal] = useState(value);
  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        autoFocus
        type="text"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSave(val); if (e.key === 'Escape') onCancel(); }}
        className="flex-1 min-w-0 px-2 py-1 text-sm rounded border"
        style={{ borderColor: 'var(--primary-solid)', outline: 'none' }}
      />
      <button onClick={() => onSave(val)} className="p-1 rounded hover:bg-green-50" title="Valider">
        <Check size={14} style={{ color: '#22C55E' }} />
      </button>
      <button onClick={onCancel} className="p-1 rounded hover:bg-gray-100" title="Annuler">
        <X size={14} style={{ color: 'var(--text-muted)' }} />
      </button>
    </div>
  );
}

// ─── New Item Row (inline form to add) ───────────────────────────────────────
function NewItemRow({ placeholder, onSave, onCancel }) {
  const [val, setVal] = useState('');
  const handleSave = () => { if (val.trim()) onSave(val.trim()); };
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-subtle)' }}>
      <input
        autoFocus
        type="text"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
        className="flex-1 px-2 py-1 text-sm rounded border"
        style={{ borderColor: 'var(--primary-solid)' }}
        placeholder={placeholder}
      />
      <button onClick={handleSave} disabled={!val.trim()}
        className="px-3 py-1 text-xs font-semibold text-white rounded"
        style={{ background: val.trim() ? 'var(--primary-gradient)' : 'var(--text-disabled)' }}>
        Ajouter
      </button>
      <button onClick={onCancel} className="p-1 rounded hover:bg-gray-100">
        <X size={14} style={{ color: 'var(--text-muted)' }} />
      </button>
    </div>
  );
}

export default function MarquesModeles() {
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [selectedMarque, setSelectedMarque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marqueSearch, setMarqueSearch] = useState('');
  const [modeleSearch, setModeleSearch] = useState('');

  // Inline edit/add state
  const [editingMarqueId, setEditingMarqueId] = useState(null);
  const [editingModeleId, setEditingModeleId] = useState(null);
  const [showNewMarque, setShowNewMarque] = useState(false);
  const [showNewModele, setShowNewModele] = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const loadMarques = useCallback(async () => {
    try {
      const res = await api.getMarques(marqueSearch ? { search: marqueSearch } : {});
      setMarques(res.data);
      // Auto-select first brand if none selected
      setSelectedMarque(prev => {
        if (!prev && res.data.length > 0) return res.data[0];
        if (prev) return res.data.find(m => m.marque_id === prev.marque_id) || (res.data[0] || null);
        return null;
      });
    } catch { toast.error('Erreur chargement marques'); }
    finally { setLoading(false); }
  }, [marqueSearch]);

  const loadModeles = useCallback(async () => {
    if (!selectedMarque) { setModeles([]); return; }
    try {
      const res = await api.getModeles({ marque_id: selectedMarque.marque_id });
      setModeles(res.data);
    } catch { toast.error('Erreur chargement modèles'); }
  }, [selectedMarque]);

  useEffect(() => { loadMarques(); }, [loadMarques]);
  useEffect(() => { loadModeles(); }, [loadModeles]);

  // ── Marques CRUD ──────────────────────────────────────────────────────────────
  const handleAddMarque = async (nom) => {
    try {
      await api.createMarque({ nom });
      toast.success('Marque ajoutée');
      setShowNewMarque(false);
      await loadMarques();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateMarque = async (marque_id, nom) => {
    if (!nom.trim()) { setEditingMarqueId(null); return; }
    try {
      await api.updateMarque(marque_id, { nom: nom.trim() });
      toast.success('Marque renommée');
      setEditingMarqueId(null);
      await loadMarques();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteMarque = async (marque) => {
    if (!window.confirm(`Supprimer "${marque.nom}" et ses ${marque.modeles_count || 0} modèle(s) ?`)) return;
    try {
      await api.deleteMarque(marque.marque_id);
      toast.success('Marque supprimée');
      if (selectedMarque?.marque_id === marque.marque_id) setSelectedMarque(null);
      await loadMarques();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ── Modèles CRUD ──────────────────────────────────────────────────────────────
  const handleAddModele = async (nom) => {
    if (!selectedMarque) return;
    try {
      await api.createModele({ nom, marque_id: selectedMarque.marque_id });
      toast.success('Modèle ajouté');
      setShowNewModele(false);
      await Promise.all([loadModeles(), loadMarques()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdateModele = async (modele_id, nom) => {
    if (!nom.trim()) { setEditingModeleId(null); return; }
    try {
      await api.updateModele(modele_id, { nom: nom.trim(), marque_id: selectedMarque.marque_id });
      toast.success('Modèle renommé');
      setEditingModeleId(null);
      await loadModeles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteModele = async (modele) => {
    try {
      await api.deleteModele(modele.modele_id);
      toast.success('Modèle supprimé');
      await Promise.all([loadModeles(), loadMarques()]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────────
  const filteredModeles = modeleSearch
    ? modeles.filter(m => m.nom.toLowerCase().includes(modeleSearch.toLowerCase()))
    : modeles;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
          Marques & Modèles
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          Gérez votre catalogue de marques et modèles d'équipements
        </p>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-4" style={{ minHeight: '520px' }}>

        {/* ── LEFT: Marques ── */}
        <div className="w-80 flex-shrink-0 bg-white rounded-xl border flex flex-col overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>

          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0"
            style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2">
              <Tag size={15} style={{ color: 'var(--primary-solid)' }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--text)',  }}>
                Marques
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                {marques.length}
              </span>
            </div>
            <button
              data-testid="add-marque-btn"
              onClick={() => { setShowNewMarque(true); setEditingMarqueId(null); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title="Nouvelle marque">
              <Plus size={15} style={{ color: 'var(--primary-solid)' }} />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-placeholder)' }} />
              <input type="text" value={marqueSearch} onChange={e => setMarqueSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border"
                style={{ borderColor: 'var(--text-disabled)' }}
                placeholder="Rechercher..." data-testid="marque-search-input" />
            </div>
          </div>

          {/* New marque form */}
          {showNewMarque && (
            <NewItemRow placeholder="Nom de la marque..." onSave={handleAddMarque} onCancel={() => setShowNewMarque(false)} />
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {marques.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Tag size={28} className="mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {marqueSearch ? 'Aucune marque trouvée' : 'Aucune marque'}
                </p>
                {!marqueSearch && (
                  <button onClick={() => setShowNewMarque(true)}
                    className="mt-3 text-xs font-medium" style={{ color: 'var(--primary-solid)' }}>
                    + Ajouter une marque
                  </button>
                )}
              </div>
            ) : (
              marques.map(marque => {
                const isSelected = selectedMarque?.marque_id === marque.marque_id;
                const isEditing = editingMarqueId === marque.marque_id;
                return (
                  <div
                    key={marque.marque_id}
                    data-testid="marque-row"
                    onClick={() => !isEditing && setSelectedMarque(marque)}
                    className={`flex items-center gap-2 px-3 py-3 cursor-pointer group transition-colors border-b
                      ${isSelected ? 'bg-green-50' : 'hover:bg-gray-50'}`}
                    style={{ borderColor: 'var(--border-light)' }}>
                    {isEditing ? (
                      <InlineEdit
                        value={marque.nom}
                        onSave={(nom) => handleUpdateMarque(marque.marque_id, nom)}
                        onCancel={() => setEditingMarqueId(null)}
                      />
                    ) : (
                      <>
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <span className="text-sm font-medium truncate" style={{ color: isSelected ? 'var(--primary-solid)' : 'var(--text)' }}>
                            {marque.nom}
                          </span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: isSelected ? 'var(--primary-100)' : 'var(--bg)', color: isSelected ? 'var(--primary-solid)' : 'var(--text-muted)', border: '1px solid var(--border)' }}>
                            {marque.modeles_count || 0}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={e => e.stopPropagation()}>
                          <button onClick={() => { setEditingMarqueId(marque.marque_id); setShowNewMarque(false); }}
                            className="p-1.5 hover:bg-blue-50 rounded" title="Renommer"
                            data-testid="edit-marque-btn">
                            <Edit2 size={13} style={{ color: 'var(--primary-light)' }} />
                          </button>
                          <button onClick={() => handleDeleteMarque(marque)}
                            className="p-1.5 hover:bg-red-50 rounded" title="Supprimer"
                            data-testid="delete-marque-btn">
                            <Trash2 size={13} style={{ color: '#DC2626' }} />
                          </button>
                        </div>
                        {isSelected && <ChevronRight size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── RIGHT: Modèles ── */}
        <div className="flex-1 bg-white rounded-xl border flex flex-col overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>

          {selectedMarque ? (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0"
                style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  <Package size={15} style={{ color: 'var(--primary-solid)' }} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)',  }}>
                    Modèles —
                    <span className="ml-1" style={{ color: 'var(--primary-solid)' }}>{selectedMarque.nom}</span>
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded-full"
                    style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                    {modeles.length}
                  </span>
                </div>
                <button
                  data-testid="add-modele-btn"
                  onClick={() => { setShowNewModele(true); setEditingModeleId(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                  style={{ background: 'var(--primary-gradient)' }}>
                  <Plus size={13} /> Nouveau modèle
                </button>
              </div>

              {/* Search */}
              <div className="px-4 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-placeholder)' }} />
                  <input type="text" value={modeleSearch} onChange={e => setModeleSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border"
                    style={{ borderColor: 'var(--text-disabled)' }}
                    placeholder="Rechercher un modèle..." data-testid="modele-search-input" />
                </div>
              </div>

              {/* New modele form */}
              {showNewModele && (
                <NewItemRow
                  placeholder={`Nom du modèle (${selectedMarque.nom})...`}
                  onSave={handleAddModele}
                  onCancel={() => setShowNewModele(false)}
                />
              )}

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {filteredModeles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center px-4">
                    <Package size={36} className="mb-3" style={{ color: 'var(--text-disabled)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)',  }}>
                      {modeleSearch ? 'Aucun modèle trouvé' : 'Aucun modèle pour cette marque'}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
                      {!modeleSearch && 'Cliquez sur "+ Nouveau modèle" pour commencer'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y" style={{ '--tw-divide-color': 'var(--border-light)' }}>
                    {filteredModeles.map(modele => (
                      <div key={modele.modele_id}
                        data-testid="modele-row"
                        className="flex items-center gap-3 px-4 py-3 group hover:bg-gray-50 transition-colors">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: 'var(--primary-50)' }}>
                          <Package size={13} style={{ color: 'var(--primary-solid)' }} />
                        </div>
                        {editingModeleId === modele.modele_id ? (
                          <InlineEdit
                            value={modele.nom}
                            onSave={(nom) => handleUpdateModele(modele.modele_id, nom)}
                            onCancel={() => setEditingModeleId(null)}
                          />
                        ) : (
                          <>
                            <span className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{modele.nom}</span>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => { setEditingModeleId(modele.modele_id); setShowNewModele(false); }}
                                className="p-1.5 hover:bg-blue-50 rounded" title="Renommer"
                                data-testid="edit-modele-btn">
                                <Edit2 size={13} style={{ color: 'var(--primary-light)' }} />
                              </button>
                              <button
                                onClick={() => handleDeleteModele(modele)}
                                className="p-1.5 hover:bg-red-50 rounded" title="Supprimer"
                                data-testid="delete-modele-btn">
                                <Trash2 size={13} style={{ color: '#DC2626' }} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Empty state — no brand selected */
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-14">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'var(--primary-50)' }}>
                <Tag size={28} style={{ color: 'var(--primary-solid)' }} />
              </div>
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text)',  }}>
                Sélectionnez une marque
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Cliquez sur une marque à gauche pour voir et gérer ses modèles
              </p>
              {marques.length === 0 && (
                <button onClick={() => setShowNewMarque(true)}
                  className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: 'var(--primary-gradient)' }}>
                  <Plus size={14} /> Créer une première marque
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
